import { z } from "zod"
import {
  productVisualDispatchClassSchema,
  type ProductVisualDispatchAttempt,
} from "./product-visual-dispatch"
import type { ProductNutritionCoverage } from "./schemas"

export const PRODUCT_VISUAL_PIM_BASELINE_PAGE_SIZE = 100

/** Product-visual constants that more than one app must agree on, plus the
 *  config-scoped gallery contract (issue #251), shared by
 *  `GET /api/products/:sku/visuals` (`apps/api/src/modules/product-visuals/config-visuals.ts`)
 *  and the product page's Visuals view
 *  (`apps/web/src/services/use-product-config-visuals.ts`).
 *
 *  `visualKind`, the gap reasons, and the target-gap reasons stay plain strings
 *  here on purpose: their vocabularies are owned elsewhere — the template
 *  registry (`@nova-ai/product-visual-templates`) and the product-visuals module
 *  — and re-declaring them in this package would be a second source to drift. */

/** Renders the BATCH DISPATCHER may have in flight at the worker at once
 *  (issue #350).
 *
 *  This is not a number of its own: it IS the render worker's Cloud Run
 *  `max_instance_count`. Request concurrency is pinned at 1 — one Chromium per
 *  container — so one instance is one render slot and the instance count is the
 *  concurrency. Three places carry it and must agree:
 *  `createRenderGate` in `apps/api/src/modules/product-visuals/render-job.ts`
 *  (the batch dispatcher's gate, which admits this many),
 *  `infra/application-cloudrun.tf`, and `scripts/deploy-render-worker.sh` (sets
 *  and then verifies it on every deploy).
 *  `apps/render-worker/src/deploy-scaling.test.ts` fails when they drift.
 *  Dispatching above the instance count buys nothing: the surplus queues inside
 *  Cloud Run and comes back as a 429 or a 60s render timeout, each after a
 *  source resolve and a content write have already been spent.
 *
 *  It bounds the batch only. The per-simple preview path
 *  (`apps/api/src/modules/product-visuals/preview.ts`) renders outside this gate,
 *  and its only bound is client-side: one render at a time per gallery session
 *  (`apps/web/src/hooks/use-product-visual-render.ts`). A gallery click during a
 *  batch is therefore one render above the slot count.
 *
 *  **5, from the 2026-08-10 measurement** (job `5de6a1e3-d3dc-436b-8412-a60d997d1f98`,
 *  100 `nutrition` items on the deployed pair): median `workerCall` 1,480 ms, so
 *  the floor is 1,480/N ms per item. N = 5 is ~296 ms/item — one store (~1,870
 *  candidates) in ~9.2 min, clearing #350's 10-minute target with margin. 6
 *  would shave ~1.5 min, but #350 expects the constraint to move off Chromium to
 *  Postgres or GCS around N ≈ 6, so the linear model stops being evidence
 *  exactly where 6 starts. **Re-measure `result.timing` before raising it** — the
 *  batch reports its own `renderConcurrency` and per-stage medians for that
 *  purpose. */
export const PRODUCT_VISUAL_RENDER_CONCURRENCY = 5

/** Rounds ONE store's drain may render before the round cap stops it (issue #353).
 *
 *  Since #353 the plan→render round loop runs INSIDE the Cloud Run Job execution, one
 *  execution per store, so this cap is no longer a CLI-only backstop: it is the number
 *  the job's Terraform `timeout` is sized against. Three places carry it and must
 *  agree — this constant, `infra/application-cloudrun.tf`, and
 *  `apps/api/src/modules/product-visuals/render-execution.test.ts`, which DERIVES the
 *  Terraform seconds from this, `PRODUCT_VISUAL_BATCH_CEILING` and
 *  `PRODUCT_VISUAL_RENDER_CONCURRENCY` and fails on the Terraform line when any of the
 *  three moves.
 *
 *  **25, and the size only has to clear a healthy store.** The biggest store measured
 *  (~1,770 remaining `nutrition` candidates for `gymbeamsk`, 2026-08-10) is 18 rounds at
 *  the 100-item ceiling, so 25 leaves room for growth without pretending to be a
 *  termination condition — the candidate-progress guard is that, and it stops a stuck
 *  drain long before the cap. `--max-rounds` may only LOWER it: a higher value would put
 *  the execution over the job timeout it was derived from, so the API refuses it.
 *
 *  It replaced a second, larger `--all` cap (250). `--all` is now one execution per
 *  registered store, so every execution carries this same per-store number. */
export const PRODUCT_VISUAL_STORE_ROUND_CAP = 25

/** Why a drain stopped (issue #353). The first three are the loop's own outcomes and
 *  were the CLI's `LoopStop` before the loop moved into the execution; `plan` is the
 *  fourth because a single explicit plan (`nova product-visuals render <planId>`) is the
 *  same execution with exactly one round and no re-planning.
 *
 *  It lives here, beside {@link PRODUCT_VISUAL_STORE_ROUND_CAP}, because the API WRITES
 *  it onto the job row and the CLI BRANCHES on it: `nova product-visuals loop` decides
 *  from these four strings whether a store drained and therefore whether the fleet run
 *  exits 0. Re-declaring the vocabulary CLI-side made a rename on the API side a silent
 *  green run instead of a type error, which is exactly the drift this package exists to
 *  stop. */
export const PRODUCT_VISUAL_DRAIN_STOP = {
  /** A fresh plan selected 0 — the store is drained. The success path. */
  drained: "drained",
  /** A round did not shrink `candidateTotal`: the same items keep failing. */
  noProgress: "no_progress",
  /** The per-store round cap was reached with candidates still left. */
  maxRounds: "max_rounds",
  /** One named plan was rendered and the execution stopped; it never re-plans. */
  plan: "plan",
} as const

export type ProductVisualDrainStop =
  (typeof PRODUCT_VISUAL_DRAIN_STOP)[keyof typeof PRODUCT_VISUAL_DRAIN_STOP]

/** The two LIVE `product_visual_images` statuses. `outdated` is the last good
 *  image kept after its inputs became incomplete; current-image consumers skip
 *  it. The ledger's third status, `superseded`, is history and never reaches a
 *  read surface — hence a narrower type than the DB column's. */
export type ProductVisualLiveImageStatus = "current" | "outdated"

/** Where one simple × visual kind stands after comparing the current deterministic
 *  render inputs with immutable artifact provenance. */
export const PRODUCT_VISUAL_FRESHNESS = {
  current: "current",
  stale: "stale",
  new: "new",
  gap: "gap",
  excluded: "excluded",
} as const

export type ProductVisualFreshness =
  (typeof PRODUCT_VISUAL_FRESHNESS)[keyof typeof PRODUCT_VISUAL_FRESHNESS]

/** The immutable artifact behind a Current or Stale cell, including the content
 *  provenance captured when it was rendered. */
export type ProductVisualPublishedImage = {
  status: ProductVisualLiveImageStatus
  url: string
  fingerprint: string
  /** Full SHA-256 of encoded JPEG bytes. Null marks readable legacy history. */
  artifactHash: string | null
  /** Template version persisted with this image. */
  templateVersion: number
  /** Running registry version for this visual kind in the API serving the read. */
  currentTemplateVersion: number
  width: number
  height: number
  createdAt: string
  source: string | null
  attributionMode: string | null
  aiUsed: boolean | null
}

export type ProductVisualInputDiffChange = {
  path: string
  before: string
  after: string
}

export type ProductVisualInputDiff =
  | { state: "available"; changes: ProductVisualInputDiffChange[]; truncated: boolean }
  | { state: "unavailable" }

export const productVisualReplacementWorkSchema = z.object({
  id: z.string(),
  mode: z.enum(["automatic", "manual"]),
  priority: productVisualDispatchClassSchema,
  priorityOrigin: z.enum(["declared", "legacy"]),
  previousAttemptId: z.string().nullable(),
  attemptNumber: z.number(),
  automaticRetryEnabled: z.boolean(),
  retryDueAt: z.string().nullable(),
  retryCause: z.string().nullable(),
  retryResolution: z.string().nullable(),
  planId: z.string().nullable(),
  renderJobId: z.string().nullable(),
  targetFingerprint: z.string(),
  trigger: z.enum(["pim_event", "magento_event", "reconciliation", "manual", "recovery"]),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  outcome: z.enum(["published", "superseded", "refused", "failed"]).nullable(),
  failureCode: z
    .enum([
      "dispatch_rejected",
      "render_failed",
      "execution_interrupted",
      "execution_error",
      "automatic_scope_unsupported",
      "interruption_retry_exhausted",
      "publication_statement_timeout",
      "publication_lock_timeout",
    ])
    .nullable(),
  detectedAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
})
export type ProductVisualReplacementWork = z.infer<typeof productVisualReplacementWorkSchema>

export type ProductVisualRegenerationResponse = {
  dispatch: ProductVisualDispatchAttempt | null
  accepted: boolean
  dispatched: boolean
  replacement: ProductVisualReplacementWork | null
}

export type ProductVisualRecoveryResponse = {
  selected: number
  queued: number
  rendered: number
  failed: number
  blockedTerminalFailures: number
  skipped: number
  remaining: number
  dispatchedReplacementId: string | null
  replacements: ProductVisualReplacementWork[]
}

/** Prefix of a `filled_from.trace` entry naming a CATALOG-MIRROR COLUMN a fill read
 *  (issue #349). Scoped to `marketing-ingredients`: it is the only kind whose filler
 *  emits these entries, and the name says so rather than implying a convention every
 *  kind follows.
 *
 *  It lives in `shared` because both sides of the ONE wire that carries it need it:
 *  the API writes the entries (`product-visuals/fill-marketing-ingredients.ts`) and
 *  the dashboard prints the columns back (`product-visual-cell-card.tsx`). A web-side
 *  string convention copied out of the API is a convention free to drift.
 *
 *  Why only this kind: it runs on a STAND-IN column until Magento ships its
 *  attribute, and the stand-in is invisible in the pixels by construction — the card
 *  reuses the `ingredients` title, so the legal list and marketing prose render
 *  identically. Naming the column is the only way a human opening a preview can tell
 *  which they have. `etiquette` reads the same three mirror columns and would benefit
 *  equally, but it stamps only `magento_eav:selected_simple_first` and was left alone
 *  by #349; widening this to a second producer is a deliberate change, not a
 *  discovery. */
export const MARKETING_INGREDIENTS_MIRROR_COLUMN_TRACE_PREFIX = "mirror_column:"

/** The mirror columns a `filled_from.trace` names, in trace order. Empty for every
 *  kind but `marketing-ingredients`, which is the only emitter — including
 *  `etiquette`, whose fill DOES read mirror columns but does not name them. */
export function marketingIngredientsMirrorColumns(trace: readonly string[]): string[] {
  return trace.flatMap((entry) =>
    entry.startsWith(MARKETING_INGREDIENTS_MIRROR_COLUMN_TRACE_PREFIX)
      ? [entry.slice(MARKETING_INGREDIENTS_MIRROR_COLUMN_TRACE_PREFIX.length)]
      : []
  )
}

export type ProductVisualCell = {
  visualKind: string
  freshness: ProductVisualFreshness
  artifact: ProductVisualPublishedImage | null
  currentFingerprint: string | null
  reason: string | null
  detail: string | null
  diff: ProductVisualInputDiff | null
  replacement: ProductVisualReplacementWork | null
  /** Newest-first exact attempt evidence across prior fingerprints too. */
  replacementHistory?: ProductVisualReplacementWork[]
}

export type ProductVisualSimple = {
  simpleSku: string
  flavor: string | null
  size: string | null
  /** Set when the simple is not a render target at all (`product_not_live`,
   *  `magento_id_missing`, `master_category_not_renderable`,
   *  `catalog_missing_master_category`, …); `kinds` is then empty — the one dead end
   *  where no render is offered. Brand is NOT one of these since issue #337: it is
   *  scoped per visual kind, so it arrives as one cell's `gap` reason instead.
   *
   *  The last two are the same refusal with different remedies and so are two codes,
   *  never one: an out-of-scope category is a fact about the product, while a missing
   *  one is a catalog not yet refreshed with its category mapping. */
  targetGap: string | null
  kinds: ProductVisualCell[]
}

export type ProductConfigVisuals = {
  configSku: string
  store: string
  locale: string
  /** Every simple of the config in this store; `simples` is capped below it. */
  total: number
  /** Strict coverage over this store's live nutrition variants. Null when the
   * product is outside nutrition scope or has no live variant. */
  nutritionCoverage: ProductNutritionCoverage | null
  simples: ProductVisualSimple[]
}

export const PRODUCT_PHOTO_SOURCE_ROLE = {
  main: "main",
  sharedSecondary: "shared_secondary",
} as const

export type ProductPhotoSourceRole =
  (typeof PRODUCT_PHOTO_SOURCE_ROLE)[keyof typeof PRODUCT_PHOTO_SOURCE_ROLE]

export const PRODUCT_PHOTO_CUTOUT_STATE = {
  available: "available",
  stale: "stale",
  failed: "failed",
} as const

export type ProductPhotoCutoutState =
  (typeof PRODUCT_PHOTO_CUTOUT_STATE)[keyof typeof PRODUCT_PHOTO_CUTOUT_STATE]

export const PRODUCT_PHOTO_CUTOUT_JOB_STATUS = {
  queued: "queued",
  running: "running",
  succeeded: "succeeded",
  failed: "failed",
} as const

export type ProductPhotoCutoutJobStatus =
  (typeof PRODUCT_PHOTO_CUTOUT_JOB_STATUS)[keyof typeof PRODUCT_PHOTO_CUTOUT_JOB_STATUS]

export type ProductPhotoCutoutJob = {
  id: string
  store: string
  simpleSku: string
  sourceKey: string
  status: ProductPhotoCutoutJobStatus
  outcome: "gap" | "superseded" | "accepted" | "refused" | null
  reused: boolean | null
  assetId: string | null
  resultReason: string | null
  error: { kind: string; message: string } | null
  createdAt: string
  updatedAt: string
  finishedAt: string | null
}

export type ProductPhotoRetainedUsage = {
  consumerType: string
  label: string
  approvalIds: string[]
  isStale: boolean
}

/** One immutable cutout identity. `available` means it may receive a new draft
 * Slot binding; stale and failed versions remain visible for audit. */
export type ProductPhotoCutout = {
  id: string
  state: ProductPhotoCutoutState
  fingerprint: string
  artifactHash: string | null
  sourceDigest: string
  url: string | null
  width: number | null
  height: number | null
  failureReason: string | null
  createdAt: string
  retainedUsageCount: number
  retainedUsage: ProductPhotoRetainedUsage[]
  retainedUsageTruncated: boolean
}

export type ProductPhotoSource = {
  sourceKey: string
  role: ProductPhotoSourceRole
  position: number
  url: string
  /** False when Magento no longer exposes this exact URL for the product. */
  isCurrent: boolean
  /** Main is automatic. A secondary becomes true only after an explicit write. */
  isAssigned: boolean
  /** Latest active request, otherwise the latest terminal request, for this source. */
  latestJob: ProductPhotoCutoutJob | null
  cutouts: ProductPhotoCutout[]
}

export type ProductPhotoSimple = {
  simpleSku: string
  flavor: string | null
  size: string | null
  sources: ProductPhotoSource[]
}

export type ProductPhotos = {
  configSku: string
  store: string
  total: number
  simplesTruncated: boolean
  sourcesTruncated: boolean
  assignmentsTruncated: boolean
  cutoutsTruncated: boolean
  jobsTruncated: boolean
  usageTruncated: boolean
  approvalContextTruncated: boolean
  simples: ProductPhotoSimple[]
}
